import {
    round2,
} from "./colors";
import type {
    ParsedConnectionObject,
    ShapeOutline,
} from "./geometry";
import {
    applyHeatColor,
    arcOutline,
    bridgedConnectionChains,
    chainConnectionPaths,
    concentricRingRadii,
    connectionObjectPlacements,
    connectionObjectTransform,
    diagramArcPath,
    ellipseOutline,
    ellipsePath,
    groupOutlinePath,
    lineOutline,
    linePath,
    nodeShapeGeometry,
    nodeShapeSvg,
    parseConnectionObject,
    polygonOutline,
    polygonPath,
    presetStrokeObjectPlacements,
    presetStrokeShapePaths,
    radialEmitterSegments,
    randomObjectIndex,
    recolorObjectForHeat,
    rectCenter,
    rectOutline,
    rectPath,
    rotateTransformAttr,
} from "./geometry";
import type {
    ConnectionPreset,
    DiagramDocument,
    DiagramHeatZone,
    DiagramLayer,
    DiagramNode,
    DiagramPattern,
    Fill,
    OutlineProperty,
    StrokeStyle,
} from "./types";
import {
    resolveGroupOutline,
} from "./types";

// --- render to SVG ----------------------------------------------------------

export interface DiagramRenderOptions {
    presets: ConnectionPreset[];
    outputWidth: number;
    outputHeight: number;
    /** Background fill for the exported SVG (null = transparent). */
    background?: string | null;
}

function strokeAttrs(
    style: Pick<StrokeStyle, "color" | "strokeWidth" | "dash"> | undefined,
): string {
    const color = style?.color ?? "#a0c4ff";
    const width = style?.strokeWidth ?? 1.5;
    const dash = style?.dash ? ` stroke-dasharray="${style.dash}"` : "";
    return `stroke="${color}" stroke-width="${width}"${dash}`;
}

/**
 * SVG element strings for a shape outline styled by a stroke style: the style's
 * parallel stroked lines and/or objects-along-path, populated on the shape's own
 * `outline` (see `presetStrokeShapePaths` / `presetStrokeObjectPlacements`).
 * `fill` paints the first (innermost) stroke path so a filled shape keeps its
 * interior; the other lines are stroke-only. `seedKey` (the shape id) seeds
 * random object order. Used by the exporter for every standalone shape that
 * carries a `strokeStyleId`; the live viewer mirrors it.
 */
function presetStrokeShapeEls(
    outline: ShapeOutline,
    style: StrokeStyle,
    fill: string,
    seedKey: string,
    heatZones: ReadonlyArray<DiagramHeatZone> = [],
): string[] {
    if (outline.points.length < 2) return [];
    const kind = style.renderKind ?? "stroke";
    const objectsAvailable =
        (style.objectSvgs?.length ?? 0) > 0 &&
        (style.objectSpacing ?? 0) > 0;
    const drawStroke =
        kind === "stroke" || kind === "both" || !objectsAvailable;
    const els: string[] = [];
    if (drawStroke) {
        const paths = presetStrokeShapePaths(
            outline.points,
            outline.closed,
            style,
        );
        paths.forEach((d, i) => {
            // Only the first line carries the fill (the shape's interior); the
            // rest are stroke-only so parallel lines don't each re-fill.
            const fillAttr = i === 0 ? fill : "none";
            els.push(
                `<path d="${d}" fill="${fillAttr}" ${strokeAttrs(style)} stroke-linecap="round" stroke-linejoin="round" />`,
            );
        });
    }
    if ((kind === "objects" || kind === "both") && objectsAvailable) {
        const parsed = style.objectSvgs.map((o) => parseConnectionObject(o.svg));
        for (const p of presetStrokeObjectPlacements(
            outline.points,
            outline.closed,
            style,
            seedKey,
        )) {
            const obj = parsed[p.objectIndex];
            if (!obj) continue;
            const transform = connectionObjectTransform(p, obj, style);
            const inner = recolorObjectForHeat(
                obj.inner,
                style,
                { x: p.x, y: p.y },
                heatZones,
            );
            els.push(`<g transform="${transform}">${inner}</g>`);
        }
    }
    return els;
}

/** SVG element id for a pattern's `<pattern>` def (and `url(#…)` reference). */
export function patternElementId(patternId: string): string {
    return `pattern-${patternId}`;
}

/**
 * Resolve a `Fill | null` to an SVG `fill` paint string: `"none"` for no fill, a
 * color for solid fills, or `url(#pattern-…)` for a pattern fill that still
 * names a known pattern. When a fill resolves to a pattern, its id is recorded
 * in `usedPatternIds` so only referenced patterns are emitted into `<defs>`.
 * Unknown pattern references fall back to `"none"`.
 */
export function resolveFillPaint(
    fill: Fill | null,
    patternById: Map<string, DiagramPattern>,
    usedPatternIds?: Set<string>,
): string {
    if (!fill) return "none";
    if (fill.type === "solid") {
        return fill.color && fill.color !== "transparent"
            ? fill.color
            : "none";
    }
    if (!patternById.has(fill.patternId)) return "none";
    usedPatternIds?.add(fill.patternId);
    return `url(#${patternElementId(fill.patternId)})`;
}

/** Deterministic 32-bit hash of a string, used to seed a pattern's shuffle. */
function hashStringToSeed(str: string): number {
    let h = 2166136261;
    for (let i = 0; i < str.length; i += 1) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/**
 * One cell of a pattern's super-tile: the (centered, scaled) artwork for the
 * object assigned to grid position `index`, translated to its cell.
 */
function patternCellMarkup(
    parsed: ParsedConnectionObject,
    cellX: number,
    cellY: number,
    cellW: number,
    cellH: number,
    tile: number,
): string {
    const maxDim = Math.max(parsed.width, parsed.height) || 1;
    const scale = tile / maxDim;
    // Center the scaled artwork within the cell.
    const offX = cellX + (cellW - parsed.width * scale) / 2;
    const offY = cellY + (cellH - parsed.height * scale) / 2;
    return (
        `<g transform="translate(${round2(offX)} ${round2(offY)}) scale(${round2(scale)})">` +
        `${parsed.inner}</g>`
    );
}

/**
 * Build the inner `<pattern>` markup for one tiled-SVG pattern.
 *
 * With a single object the tile is just that object, centered in a (tile +
 * spacing) cell. With several, the objects fill a roughly square NxM "super-tile"
 * block — one object per cell — that repeats: `sequence` lays them out in order,
 * `random` shuffles the per-cell assignment with a seed derived from the
 * pattern's id (stable across re-renders). An optional solid background rect
 * fills the whole super-tile. Returns "" when no object SVG can be parsed.
 * Exported so the live viewer can build the same `<pattern>` defs it injects.
 */
export function patternDefSvg(pattern: DiagramPattern): string {
    const parsedObjects = (pattern.objects ?? [])
        .map((o) => parseConnectionObject(o.svg))
        .filter((p): p is ParsedConnectionObject => p !== null);
    const count = parsedObjects.length;
    if (count === 0) return "";

    const tile = Math.max(1, pattern.tileSize);
    const cellW = tile + Math.max(0, pattern.spacingX);
    const cellH = tile + Math.max(0, pattern.spacingY);

    // Pack objects into a roughly square block so the super-tile stays compact.
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    const tileW = cols * cellW;
    const tileH = rows * cellH;

    const order = pattern.order ?? "sequence";
    const seed = hashStringToSeed(pattern.id);

    const cells: string[] = [];
    for (let r = 0; r < rows; r += 1) {
        for (let c = 0; c < cols; c += 1) {
            const slot = r * cols + c;
            const objIndex =
                order === "random"
                    ? randomObjectIndex(seed, slot, count)
                    : slot % count;
            const parsed = parsedObjects[objIndex]!;
            cells.push(
                patternCellMarkup(
                    parsed,
                    c * cellW,
                    r * cellH,
                    cellW,
                    cellH,
                    tile,
                ),
            );
        }
    }

    const transform =
        pattern.rotationDeg !== 0
            ? ` patternTransform="rotate(${round2(pattern.rotationDeg)})"`
            : "";
    const bg =
        pattern.backgroundColor && pattern.backgroundColor !== "transparent"
            ? `<rect x="0" y="0" width="${round2(tileW)}" height="${round2(tileH)}" fill="${pattern.backgroundColor}" />`
            : "";
    return (
        `<pattern id="${patternElementId(pattern.id)}" ` +
        `patternUnits="userSpaceOnUse" ` +
        `width="${round2(tileW)}" height="${round2(tileH)}"${transform}>` +
        `${bg}${cells.join("")}</pattern>`
    );
}

/** `<defs>` block holding the `<pattern>` defs for every used pattern (or ""). */
function patternDefsSvg(
    patterns: DiagramPattern[],
    usedPatternIds: Set<string>,
): string {
    const defs = patterns
        .filter((p) => usedPatternIds.has(p.id))
        .map((p) => patternDefSvg(p))
        .filter(Boolean);
    if (defs.length === 0) return "";
    return `  <defs>\n${defs.map((d) => `    ${d}`).join("\n")}\n  </defs>\n`;
}

/**
 * Serialize the whole diagram to an SVG string. Returns "" when nothing
 * would render (e.g. an empty document). Node markers render on top of
 * connections/emitters/outlines.
 */
export function renderDiagram2DToSvg(
    doc: DiagramDocument,
    opts: DiagramRenderOptions,
): string {
    const presetById = new Map(opts.presets.map((p) => [p.id, p]));
    const styleById = new Map(doc.strokeStyles.map((s) => [s.id, s]));
    const nodeById = new Map(doc.nodes.map((n) => [n.id, n]));
    const effectById = new Map(doc.effects.map((eff) => [eff.id, eff]));
    const patternById = new Map(doc.patterns.map((p) => [p.id, p]));
    // Pattern fills resolved while building shapes record their ids here so only
    // referenced patterns are emitted into <defs>.
    const usedPatternIds = new Set<string>();

    // Build the element strings (in draw order within a layer) for one layer's
    // worth of document content. `layerDoc` is the full document narrowed to a
    // single layer's groups/shapes/connections/nodes; the shared maps above keep
    // node geometry / styles resolvable. Connection chains, effects, and group
    // outlines reference nodes by id via the document-wide `nodeById`, so a
    // group/connection on this layer still resolves its member node positions
    // (members always share the layer — see the cross-layer constraints).
    const buildLayerEls = (layerDoc: DiagramDocument): string[] => {
    const groupEls: string[] = [];
    for (const group of layerDoc.groups) {
        const ownOutline = group.properties.find(
            (p): p is OutlineProperty => p.kind === "outline",
        );
        if (!ownOutline) continue;
        const outline = resolveGroupOutline(
            ownOutline,
            doc.groupOutlinePresets,
        );
        const positions = group.nodeIds
            .map((id) => nodeById.get(id))
            .filter((n): n is DiagramNode => Boolean(n) && n!.visible)
            .map((n) => n.position);
        const d = groupOutlinePath(positions, outline);
        if (!d) continue;
        const style = outline.strokeStyleId
            ? styleById.get(outline.strokeStyleId)
            : undefined;
        const fill = resolveFillPaint(
            outline.fill,
            patternById,
            usedPatternIds,
        );
        groupEls.push(
            `<path d="${d}" fill="${fill}" ${strokeAttrs(style)} stroke-linejoin="round" />`,
        );
    }

    const ellipseEls: string[] = [];
    for (const ellipse of layerDoc.ellipses) {
        if (!ellipse.visible) continue;
        const fill = resolveFillPaint(
            ellipse.fill,
            patternById,
            usedPatternIds,
        );
        const style = ellipse.strokeStyleId
            ? styleById.get(ellipse.strokeStyleId)
            : undefined;
        if (style) {
            ellipseEls.push(
                ...presetStrokeShapeEls(
                    ellipseOutline(ellipse),
                    style,
                    fill,
                    ellipse.id,
                    doc.heatZones,
                ),
            );
            continue;
        }
        const d = ellipsePath(ellipse);
        if (!d) continue;
        const dash = ellipse.dash
            ? ` stroke-dasharray="${ellipse.dash}"`
            : "";
        const transform = rotateTransformAttr(ellipse.center, ellipse.rotation);
        ellipseEls.push(
            `<path d="${d}" fill="${fill}" stroke="${ellipse.color}" stroke-width="${ellipse.strokeWidth}"${dash} stroke-linecap="round"${transform} />`,
        );
    }

    const rectEls: string[] = [];
    for (const rect of layerDoc.rects) {
        if (!rect.visible) continue;
        const fill = resolveFillPaint(rect.fill, patternById, usedPatternIds);
        const style = rect.strokeStyleId
            ? styleById.get(rect.strokeStyleId)
            : undefined;
        if (style) {
            rectEls.push(
                ...presetStrokeShapeEls(
                    rectOutline(rect),
                    style,
                    fill,
                    rect.id,
                    doc.heatZones,
                ),
            );
            continue;
        }
        const d = rectPath(rect);
        if (!d) continue;
        const dash = rect.dash ? ` stroke-dasharray="${rect.dash}"` : "";
        const transform = rotateTransformAttr(rectCenter(rect), rect.rotation);
        rectEls.push(
            `<path d="${d}" fill="${fill}" stroke="${rect.color}" stroke-width="${rect.strokeWidth}"${dash} stroke-linejoin="round"${transform} />`,
        );
    }

    const polygonEls: string[] = [];
    for (const polygon of layerDoc.polygons) {
        if (!polygon.visible) continue;
        const fill = resolveFillPaint(
            polygon.fill,
            patternById,
            usedPatternIds,
        );
        const style = polygon.strokeStyleId
            ? styleById.get(polygon.strokeStyleId)
            : undefined;
        if (style) {
            polygonEls.push(
                ...presetStrokeShapeEls(
                    polygonOutline(polygon),
                    style,
                    fill,
                    polygon.id,
                    doc.heatZones,
                ),
            );
            continue;
        }
        const d = polygonPath(polygon);
        if (!d) continue;
        const dash = polygon.dash ? ` stroke-dasharray="${polygon.dash}"` : "";
        polygonEls.push(
            `<path d="${d}" fill="${fill}" stroke="${polygon.color}" stroke-width="${polygon.strokeWidth}"${dash} stroke-linejoin="round" />`,
        );
    }

    const lineEls: string[] = [];
    for (const line of layerDoc.lines) {
        if (!line.visible) continue;
        const style = line.strokeStyleId
            ? styleById.get(line.strokeStyleId)
            : undefined;
        if (style) {
            lineEls.push(
                ...presetStrokeShapeEls(
                    lineOutline(line),
                    style,
                    "none",
                    line.id,
                    doc.heatZones,
                ),
            );
            continue;
        }
        const d = linePath(line);
        if (!d) continue;
        const dash = line.dash ? ` stroke-dasharray="${line.dash}"` : "";
        lineEls.push(
            `<path d="${d}" fill="none" stroke="${line.color}" stroke-width="${line.strokeWidth}"${dash} stroke-linecap="round" />`,
        );
    }

    const arcEls: string[] = [];
    for (const arc of layerDoc.arcs) {
        if (!arc.visible) continue;
        const style = arc.strokeStyleId
            ? styleById.get(arc.strokeStyleId)
            : undefined;
        if (style) {
            arcEls.push(
                ...presetStrokeShapeEls(
                    arcOutline(arc),
                    style,
                    "none",
                    arc.id,
                    doc.heatZones,
                ),
            );
            continue;
        }
        const d = diagramArcPath(arc);
        if (!d) continue;
        const dash = arc.dash ? ` stroke-dasharray="${arc.dash}"` : "";
        arcEls.push(
            `<path d="${d}" fill="none" stroke="${arc.color}" stroke-width="${arc.strokeWidth}"${dash} stroke-linecap="round" />`,
        );
    }

    const connectionEls: string[] = [];
    // Group connections into chains so bridged presets render as continuous,
    // tangent/corner-matched strokes (mirrors the live viewer). Connections to
    // hidden nodes still render — a hidden node suppresses only its own shape,
    // not the geometry anchored to it. Per-connection arc overrides ride along
    // on the standalone (length-1) chains the grouping produces for them.
    const connectionChains = bridgedConnectionChains(
        layerDoc,
        nodeById,
        presetById,
    );
    for (const chain of connectionChains) {
        const kind = chain.preset.renderKind ?? "stroke";
        const objectsAvailable =
            (chain.preset.objectSvgs?.length ?? 0) > 0 &&
            (chain.preset.objectSpacing ?? 0) > 0;
        // Stroke the line unless the preset is objects-only with usable objects
        // (an objects-only preset with no objects still strokes, as a fallback).
        const drawStroke = kind === "stroke" || kind === "both" || !objectsAvailable;
        if (drawStroke) {
            for (const d of chainConnectionPaths(chain)) {
                connectionEls.push(
                    `<path d="${d}" fill="none" ${strokeAttrs(chain.preset)} stroke-linecap="round" stroke-linejoin="round" />`,
                );
            }
        }
        if ((kind === "objects" || kind === "both") && objectsAvailable) {
            const parsed = chain.preset.objectSvgs.map((o) =>
                parseConnectionObject(o.svg),
            );
            for (const p of connectionObjectPlacements(chain)) {
                const obj = parsed[p.objectIndex];
                if (!obj) continue;
                const transform = connectionObjectTransform(
                    p,
                    obj,
                    chain.preset,
                );
                const inner = recolorObjectForHeat(
                    obj.inner,
                    chain.preset,
                    { x: p.x, y: p.y },
                    doc.heatZones,
                );
                connectionEls.push(
                    `<g transform="${transform}">${inner}</g>`,
                );
            }
        }
    }

    const effectEls: string[] = [];
    for (const node of layerDoc.nodes) {
        // A hidden node suppresses only its own shape; its effects (e.g. a
        // radial emitter anchored at it) still render.
        for (const effectId of node.effectIds) {
            const effect = effectById.get(effectId);
            if (!effect) continue;
            const style = effect.strokeStyleId
                ? styleById.get(effect.strokeStyleId)
                : undefined;
            if (effect.kind === "radial-emitter") {
                for (const seg of radialEmitterSegments(node.position, effect)) {
                    effectEls.push(
                        `<line x1="${round2(seg.from.x)}" y1="${round2(seg.from.y)}" x2="${round2(seg.to.x)}" y2="${round2(seg.to.y)}" ${strokeAttrs(style)} stroke-linecap="round" />`,
                    );
                }
            } else if (effect.kind === "concentric-rings") {
                for (const r of concentricRingRadii(effect)) {
                    effectEls.push(
                        `<circle cx="${round2(node.position.x)}" cy="${round2(node.position.y)}" r="${r}" fill="none" ${strokeAttrs(style)} />`,
                    );
                }
            }
        }
    }

    const nodeEls: string[] = [];
    for (const node of layerDoc.nodes) {
        if (!node.visible) continue;
        const geom = nodeShapeGeometry(node.position, node.radius, node.shape);
        const color = node.heatReactive
            ? applyHeatColor(node.color, node.position, doc.heatZones)
            : node.color;
        nodeEls.push(nodeShapeSvg(geom, color));
    }

    // Within a layer, the element types stack in the same order they always
    // have (group outlines at the back, node markers on top).
    return [
        ...groupEls,
        ...rectEls,
        ...polygonEls,
        ...ellipseEls,
        ...lineEls,
        ...arcEls,
        ...connectionEls,
        ...effectEls,
        ...nodeEls,
    ];
    };

    // Narrow the document to a single layer's content. Document-wide reference
    // collections (strokeStyles/presets/effects/patterns) and the full node
    // graph stay intact so geometry and styles still resolve; only the
    // per-layer element lists are filtered.
    const layerView = (layer: DiagramLayer): DiagramDocument => ({
        ...doc,
        nodes: doc.nodes.filter((n) => n.layerId === layer.id),
        connections: doc.connections.filter((c) => c.layerId === layer.id),
        groups: doc.groups.filter((g) => g.layerId === layer.id),
        ellipses: doc.ellipses.filter((e) => e.layerId === layer.id),
        lines: doc.lines.filter((l) => l.layerId === layer.id),
        arcs: doc.arcs.filter((a) => a.layerId === layer.id),
        rects: doc.rects.filter((r) => r.layerId === layer.id),
        polygons: doc.polygons.filter((p) => p.layerId === layer.id),
    });

    // Layers render back-to-front in document order; hidden layers are skipped
    // entirely. Each visible, non-empty layer becomes a wrapping <g> so the
    // exported SVG mirrors the layer structure.
    const layerEls: string[] = [];
    for (const layer of doc.layers) {
        if (!layer.visible) continue;
        const els = buildLayerEls(layerView(layer));
        if (els.length === 0) continue;
        const safeName = layer.name.replace(/"/g, "&quot;");
        layerEls.push(
            `  <g data-layer="${safeName}">\n${els.join("\n")}\n  </g>`,
        );
    }

    if (layerEls.length === 0) return "";

    const defs = patternDefsSvg(doc.patterns, usedPatternIds);
    const bg =
        opts.background && opts.background !== "transparent"
            ? `<rect x="0" y="0" width="${opts.outputWidth}" height="${opts.outputHeight}" fill="${opts.background}" />\n`
            : "";

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${opts.outputWidth} ${opts.outputHeight}">
${defs}${bg}  <g class="svg-diagram-2d">
${layerEls.join("\n")}
  </g>
</svg>`;
}
